import { useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Form, Input, Result, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { InboxOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const MAX_TOTAL_BYTES = 150 * 1024 * 1024; // 150 MB, mirrors the backend limit

interface ClaimFormValues {
  company_name: string;
  email: string;
  invoice_number: string;
  product_name_size: string;
  quantity: string;
  color_finishing: string;
  material: string;
  description: string;
  label_file?: UploadFile[];
  issue_photos?: UploadFile[];
}

const TEXT_FIELDS = [
  'company_name',
  'email',
  'invoice_number',
  'product_name_size',
  'quantity',
  'color_finishing',
  'material',
  'description',
] as const;

const fileList = (e: unknown): UploadFile[] => {
  if (Array.isArray(e)) return e;
  return (e as { fileList?: UploadFile[] })?.fileList ?? [];
};

const PublicClaimForm = () => {
  const [form] = Form.useForm<ClaimFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneCode, setDoneCode] = useState<string | null>(null);

  const onFinish = async (values: ClaimFormValues) => {
    setError(null);

    const labelFiles = fileList(values.label_file);
    const issueFiles = fileList(values.issue_photos);

    const all = [...labelFiles, ...issueFiles];
    const total = all.reduce((sum, f) => sum + (f.size ?? 0), 0);
    if (total > MAX_TOTAL_BYTES) {
      setError('Total attachment size exceeds 150 MB. Please reduce the files and try again.');
      return;
    }

    const data = new FormData();
    TEXT_FIELDS.forEach((k) => data.append(k, (values[k] as string) ?? ''));
    if (labelFiles[0]?.originFileObj) data.append('label_file', labelFiles[0].originFileObj);
    issueFiles.forEach((f) => {
      if (f.originFileObj) data.append('issue_photos', f.originFileObj);
    });

    setSubmitting(true);
    try {
      const resp = await axios.post('/api/v1/claims/submit/', data);
      setDoneCode(resp.data.code as string);
    } catch (err) {
      const detail =
        axios.isAxiosError(err) && err.response?.data
          ? typeof err.response.data === 'string'
            ? err.response.data
            : (err.response.data.detail ??
              err.response.data.non_field_errors?.[0] ??
              'Please check the form and try again.')
          : 'Network error. Please try again.';
      setError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (doneCode) {
    return (
      <div style={pageStyle}>
        <Card style={{ maxWidth: 640, width: '100%' }}>
          <Result
            status="success"
            title="Your claim has been received"
            subTitle={
              <>
                Reference number: <strong>{doneCode}</strong>
                <br />
                Our team will review your case and get back to you as soon as possible. A
                confirmation has been sent to your email.
              </>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Card style={{ maxWidth: 720, width: '100%' }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Customer Claim Form
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          We are sorry to hear that you experienced an issue with our product or service. Please
          complete all required fields and attach supporting documents and photos related to your
          claim so we can review your case as quickly as possible.
        </Paragraph>

        {error && (
          <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} closable />
        )}

        <Form form={form} layout="vertical" requiredMark onFinish={onFinish} disabled={submitting}>
          <Form.Item
            name="company_name"
            label="Full Name / Company Name"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input maxLength={255} />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'This field is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input maxLength={254} />
          </Form.Item>

          <Form.Item
            name="invoice_number"
            label="Invoice Number"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input maxLength={64} />
          </Form.Item>

          <Form.Item
            name="product_name_size"
            label="Product Name and Size"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input maxLength={512} />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input maxLength={64} />
          </Form.Item>

          <Form.Item
            name="color_finishing"
            label="Color or Finishing"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input maxLength={255} />
          </Form.Item>

          <Form.Item
            name="material"
            label="Material"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input maxLength={255} />
          </Form.Item>

          <Form.Item
            name="label_file"
            label="Product Identification Label (photo with production date)"
            valuePropName="fileList"
            getValueFromEvent={fileList}
          >
            <Upload.Dragger
              beforeUpload={() => false}
              maxCount={1}
              accept="image/*,application/pdf"
              listType="picture"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag a photo here (optional)</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description of the Issue / Claim"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <TextArea rows={5} />
          </Form.Item>

          <Form.Item
            name="issue_photos"
            label="Photos of the Issue (please show the problem in as much detail as possible)"
            valuePropName="fileList"
            getValueFromEvent={fileList}
            rules={[
              {
                validator: (_, value) =>
                  fileList(value).length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please attach at least one photo or video')),
              },
            ]}
            extra="Images or videos. Up to 150 MB in total."
          >
            <Upload.Dragger
              beforeUpload={() => false}
              multiple
              accept="image/*,video/*"
              listType="picture"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag photos / videos here</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" size="large" loading={submitting} block>
              Submit claim
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '40px 16px',
  background: '#F2EDE4',
};

export default PublicClaimForm;
